import { IStudioReleaseNote } from "@/data/redux/appSlice";
import { GameSystems, IArmyBookListItem, IArmyData, ShipClass, SortOptions } from "@/data/types";
import { orderUnits } from "./Helpers";
import PersistenceService from "./list-builder/PersistenceService";
import UnitService from "./UnitService";

export default class StudioApiService {
  public static async getArmyBooks(
    gameSystemSlug: string,
    official: boolean,
    page: number = 1,
    search: string = "",
    unitCount: number = 0,
    balanceValid: boolean = false,
    customRules: boolean = true,
    fans: boolean = false,
    sortBy: SortOptions = "votes"
  ) {
    const params = new URLSearchParams({
      filters: official ? "official" : "community",
      gameSystemSlug: gameSystemSlug,
      searchText: search,
      page: page?.toString(),
      unitCount: unitCount.toString(),
      balanceValid: balanceValid.toString(),
      customRules: customRules.toString(),
      fans: fans.toString(),
      sortBy: official ? null : sortBy
    });
    const res = await fetch(`/api/army-books?${params.toString()}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as IArmyBookListItem[];
  }

  public static async getArmyBooksForUser(gameSystemSlug: string, userId: string) {
    const params = new URLSearchParams({
      gameSystemSlug: gameSystemSlug,
      userId
    });
    const res = await fetch(`/api/army-books/user?${params.toString()}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as IArmyBookListItem[];
  }

  public static async getArmyBookData(
    armyId: string,
    gameSystem: GameSystems,
    options: { simpleMode?: boolean; includeVehicles?: boolean; swarmMode?: boolean } = {}
  ) {
    const paramsObj: any = { gameSystem: gameSystem.toString() };
    if (options.simpleMode) paramsObj.simpleMode = true;
    if (options.includeVehicles) paramsObj.includeVehicles = true;
    if (options.swarmMode) paramsObj.swarmMode = true;

    const params = new URLSearchParams(paramsObj);
    const res = await fetch(`/api/army-books/${armyId}?${params.toString()}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const transformedData: IArmyData = this.transformArmyBookData(data, armyId);
    return transformedData;
  }

  public static transformArmyBookData(data: any, armyId: string) {
    const upgradePackages = data.upgradePackages.map((upgradePackage) => ({
      ...upgradePackage,
      sections: upgradePackage.sections.map((section) => ({
        ...section,
        isCommandGroup: section.options.some((opt) =>
          opt.gains.some((g) => g.name?.toLocaleLowerCase() === "musician")
        ),
        options: section.options.map((option) => {
          const result: any = {
            ...option,
            parentSectionId: section.uid
          };
          delete result.proposedCost;
          delete result.proposedCostHint;
          delete result.proposedVersion;
          delete result.parentPackageUid;
          delete result.parentSectionUid;
          return result;
        })
      }))
    }));

    const orderedUnits = orderUnits(data.units, data.gameEngine, false);
    const units = orderedUnits.map((unit, index) => ({
      ...unit,
      armyId: armyId,
      selectedUpgrades: [],
      sortId: index,
      disabledUpgradeSections: UnitService.getDisabledUpgradeSections(unit, upgradePackages)
    }));

    const transformedData: IArmyData = {
      ...data,
      units,
      upgradePackages: upgradePackages,
      specialRules: (data.specialRules || []).concat(data.customRules || [])
    };

    return transformedData;
  }

  public static async shareList(list: any) {
    const saveKey = PersistenceService.getSaveKey(list.id);
    const payload = JSON.parse(localStorage[saveKey]).list;

    const res = await fetch("/api/share", {
      method: "POST",
      body: JSON.stringify(payload)
    });
    return { res, name: list.name };
  }

  public static async getSharedList(id: string, rangerList: boolean = false) {
    const url = rangerList ? `/api/rangers/lists/${id}` : `/api/share/${id}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  public static async getFleets() {
    const res = await fetch("/api/gfwf");
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  public static async getFleet(uid: string) {
    const res = await fetch(`/api/gfwf/${uid}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const fleet = await res.json();
    const fleetUid = fleet.name === "Common" ? "common" : fleet.uid;
    const result = {
      ...fleet,
      uid: fleetUid,
      ships: fleet.ships.map((x) => ({
        ...x,
        fleetId: fleetUid,
        squadron: x.name.toUpperCase().includes("SQUADRON"),
        class: (() => {
          switch (x.class?.toLocaleLowerCase()) {
            case "heavy":
              return ShipClass.Heavy;
            case "medium":
              return ShipClass.Medium;
            case "light":
              return ShipClass.Light;
          }
          return ShipClass.Squadron;
        })()
      }))
    };

    return result;
  }

  public static async getFlavour(name: string) {
    const url = "/api/army-books/flavour/" + encodeURIComponent(name);
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  public static async getReleaseNotes(lastVersion?: number): Promise<IStudioReleaseNote[]> {
    const res = await fetch("/api/release-notes" + (lastVersion ? `/${lastVersion}` : ""));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }
}
